import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getEvents, getEventStats } from "@/api/admin.api";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Activity, AlertTriangle, ShoppingCart, Bell, QrCode, UserPlus, LogIn, Package, Percent } from "lucide-react";

const TYPE_ICONS: Record<string, typeof Activity> = {
  "user:register": UserPlus,
  "user:login": LogIn,
  "user:login_failed": AlertTriangle,
  "order:new": ShoppingCart,
  "waiter:call": Bell,
  "menu:qr_scan": QrCode,
  "api:error": AlertTriangle,
  "item:create": Package,
  "item:update": Package,
  "item:delete": Package,
  "item:bulk_price": Percent,
};

const LEVEL_COLORS: Record<string, string> = {
  info: "bg-blue-100 text-blue-800",
  warn: "bg-yellow-100 text-yellow-800",
  error: "bg-red-100 text-red-800",
};

const TYPE_FILTERS = [
  { id: "", label: "Tumu" },
  { id: "user:register", label: "Kayit" },
  { id: "user:login", label: "Giris" },
  { id: "user:login_failed", label: "Basarisiz Giris" },
  { id: "order:new", label: "Siparis" },
  { id: "waiter:call", label: "Garson" },
  { id: "menu:qr_scan", label: "QR Okutma" },
  { id: "item:create", label: "Urun Ekleme" },
  { id: "item:update", label: "Urun Guncelleme" },
  { id: "item:delete", label: "Urun Silme" },
  { id: "item:bulk_price", label: "Toplu Fiyat" },
  { id: "api:error", label: "Hata" },
];

function timeAgo(dateStr: string) {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}sn`;
  if (diff < 3600) return `${Math.floor(diff / 60)}dk`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}sa`;
  return `${Math.floor(diff / 86400)}g`;
}

export function EventsPage() {
  const [typeFilter, setTypeFilter] = useState("");
  const [venueFilter, setVenueFilter] = useState("");
  const [page, setPage] = useState(0);
  const limit = 50;

  const { data: stats } = useQuery({
    queryKey: ["event-stats"],
    queryFn: getEventStats,
    refetchInterval: 30000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["events", typeFilter, venueFilter, page],
    queryFn: () => getEvents({ type: typeFilter || undefined, venueId: venueFilter || undefined, limit, offset: page * limit }),
    refetchInterval: 15000,
  });

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold">Olay Kayitlari</h1>

      {/* Stats */}
      {stats && (
        <div className="mb-6 grid gap-4 sm:grid-cols-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Son 1 Saat</p>
              <p className="text-2xl font-bold">{stats.lastHour}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Bugun</p>
              <p className="text-2xl font-bold">{stats.today}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Hatalar (Bugun)</p>
              <p className="text-2xl font-bold text-red-600">{stats.errorsToday}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">En Cok</p>
              <p className="text-lg font-bold">{stats.byType[0]?.type || "-"}</p>
              <p className="text-sm text-muted-foreground">{stats.byType[0]?.count || 0} kez</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex items-center gap-3">
        <Input
          placeholder="Venue ID ile filtrele..."
          value={venueFilter}
          onChange={(e) => { setVenueFilter(e.target.value.trim()); setPage(0); }}
          className="h-8 w-56"
        />
      </div>
      <div className="mb-4 flex gap-2 overflow-x-auto">
        {TYPE_FILTERS.map((f) => (
          <Badge
            key={f.id}
            variant={typeFilter === f.id ? "default" : "outline"}
            className="cursor-pointer whitespace-nowrap"
            onClick={() => { setTypeFilter(f.id); setPage(0); }}
          >
            {f.label}
          </Badge>
        ))}
      </div>

      {/* Events list */}
      {isLoading ? (
        <p className="py-8 text-center text-muted-foreground">Yukleniyor...</p>
      ) : !data?.events.length ? (
        <p className="py-8 text-center text-muted-foreground">Olay bulunamadi</p>
      ) : (
        <div className="space-y-2">
          {data.events.map((event) => {
            const Icon = TYPE_ICONS[event.type] || Activity;
            return (
              <div key={event._id} className="flex items-start gap-3 rounded-lg border p-3">
                <Icon className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{event.message}</span>
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${LEVEL_COLORS[event.level]}`}>
                      {event.level}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span>{event.type}</span>
                    <span>{timeAgo(event.createdAt)} once</span>
                    {event.ip && <span>IP: {event.ip}</span>}
                    {event.userId && <span>UID: {event.userId}</span>}
                    {event.meta && (() => { const m = event.meta as Record<string, string>; return <>
                      {m.url && <span>{m.method} {m.url}</span>}
                      {m.userName && <span>{m.userName}</span>}
                      {m.userEmail && <span>{m.userEmail}</span>}
                      {m.venueName && <span>{m.venueName}</span>}
                      {m.amount && <span>{m.amount} TRY</span>}
                    </>; })()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {data && data.total > limit && (
        <div className="mt-4 flex justify-center gap-2">
          <button
            className="rounded border px-3 py-1 text-sm disabled:opacity-50"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            Onceki
          </button>
          <span className="px-3 py-1 text-sm text-muted-foreground">
            {page * limit + 1}-{Math.min((page + 1) * limit, data.total)} / {data.total}
          </span>
          <button
            className="rounded border px-3 py-1 text-sm disabled:opacity-50"
            disabled={(page + 1) * limit >= data.total}
            onClick={() => setPage(page + 1)}
          >
            Sonraki
          </button>
        </div>
      )}
    </div>
  );
}
